import { useContext, useMemo } from 'react';
import { AppContext } from '@rabbit/app-context';
import { TenantInfo } from '@rabbit/data/types';

export type TenantBrandIdentity = {
  tenantBrandLogo?: string;
  tenantBrandName?: string;
  tenantBrandPlaceholder?: string;
};

export function resolveTenantBrandIdentity(
  tenantInfo?: TenantInfo,
  brand?: string
): TenantBrandIdentity {
  const fallback = {
    tenantBrandLogo: tenantInfo?.logo,
    tenantBrandName: tenantInfo?.name,
    tenantBrandPlaceholder: tenantInfo?.productPlaceholder,
  };
  if (!tenantInfo) return fallback;

  // Normalize brand
  const brandKey =
    brand && typeof brand === 'string' && brand.trim() !== ''
      ? brand.trim().toLowerCase()
      : '';
  if (!brandKey) return fallback;

  try {
    const raw = (tenantInfo as any)?.brandDetails;
    const detailsObj:
      | Record<
          string,
          {
            businessName?: string;
            brandLogoUrl?: string;
            authPageProductPlaceholder?: string;
          }
        >
      | undefined =
      raw && typeof raw === 'string' ? JSON.parse(raw) : (raw as any);
    if (!detailsObj) return fallback;

    // Case-insensitive lookup
    const matchedKey = Object.keys(detailsObj).find(
      (k) => k.toLowerCase() === brandKey
    );
    const info = matchedKey ? detailsObj[matchedKey] : undefined;

    return {
      tenantBrandLogo: info?.brandLogoUrl || tenantInfo.logo,
      tenantBrandName: info?.businessName || tenantInfo.name,
      tenantBrandPlaceholder:
        info?.authPageProductPlaceholder || tenantInfo.productPlaceholder,
    };
  } catch (_) {
    return fallback;
  }
}
export function useTenantBrandIdentity(opts?: {
  tenantInfo?: TenantInfo;
  brand?: string;
}): TenantBrandIdentity {
  const { tenantInfo: ctxTenantInfo } = useContext(AppContext);
  const tenantInfo =
    opts?.tenantInfo ?? (ctxTenantInfo as TenantInfo | undefined);
  const brand = opts?.brand;

  return useMemo(
    () => resolveTenantBrandIdentity(tenantInfo, brand),
    [tenantInfo, brand]
  );
}

export default useTenantBrandIdentity;
